import React, { useEffect, useState } from "react";

import classNames from "classnames";

export const DOCUMENT_EXTENSION_ICONS = [
  "word",
  "docx",
  "dotx",
  "excel",
  "odp",
  "ods",
  "odt",
  "msOffice",
  "potx",
  "powerpoint",
  "ppsx",
  "ppt",
  "pptx",
  "visio",
  "vsdx",
  "vssx",
  "vstx",
  "xls",
  "xlsx",
  "xltx",
  "pdf",
] as const;

export const AVAILABLE_ICON_NAMES = [
  "word",
  "docx",
  "dotx",
  "excel",
  "odp",
  "ods",
  "odt",
  "msOffice",
  "potx",
  "powerpoint",
  "ppsx",
  "ppt",
  "pptx",
  "visio",
  "vsdx",
  "vssx",
  "vstx",
  "xls",
  "xlsx",
  "xltx",
  "pdf",

  "add-person",
  "arrow-circle-up",
  "arrow-down",
  "arrow-up",
  "arrow-right",
  "check-circle",
  "check-circle-solid",
  "check-stroke",
  "credit-card",
  "document-add",
  "document-duplicate",
  "eye-off",
  "eye-on",
  "exclamation-circle",
  "file-pdf",
  "info-circle",
  "user-circle",
  "check-solid",
  "question-mark",
  "tooltip-circle",
  "circle-cross",
  "alert",
  "book",
  "broom",
  "calculator",
  "calendar",
  "config",
  "court",
  "cross",
  "document",
  "dollar",
  "edit",
  "email",
  "eye",
  "faq",
  "file",
  "heart",
  "home",
  "minus",
  "office",
  "phone",
  "plus",
  "percent",
  "save",
  "search",
  "settings",
  "share",
  "sliders",
  "spinner",
  "star",
  "support",
  "trash",
  "triangle",
  "undo",
  "user",
  "users",
  "download",
  "upload",
  "loader",
  "merge",
  "arrows",
  "family",
  "logo",
  "webhook",
  "duplicate",
  "key",
  "conf",
  "pin",
  "list",
  "upgrade",
  "youtube",
  "menu",
  "megaphone",
] as const;

export type IconName = (typeof AVAILABLE_ICON_NAMES)[number];
export type IconDefaultName = (typeof DOCUMENT_EXTENSION_ICONS)[number];

type IconProps = {
  name?: IconName;
  defaultName?: IconDefaultName;
  className?: string;
  spin?: boolean;
} & React.SVGProps<SVGSVGElement>;

const Icon: React.FC<IconProps> = ({ name, defaultName, className, spin, ...props }) => {
  const [IconComponent, setIconComponent] = useState<React.ComponentType<
    React.SVGProps<SVGSVGElement>
  > | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadIcon = async (): Promise<boolean> => {
      try {
        const mod = await import(`img/icons/${name}.svg?react`);
        if (isMounted) setIconComponent(() => mod.default);
        return true;
      } catch {
        return false;
      }
    };

    const loadDefaultIcon = async (): Promise<boolean> => {
      try {
        const mod = await import(`modules/wopi/icons/${defaultName}.svg?react`);
        if (isMounted) setIconComponent(() => mod.default);
        return true;
      } catch {
        return false;
      }
    };

    const load = async () => {
      if (!name && !defaultName) return;

      const found = name ? await loadIcon() : false;

      if (!found && defaultName) {
        await loadDefaultIcon();
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [name, defaultName]);

  if (!IconComponent) return null;

  return <IconComponent className={classNames("icon", { spin }, className)} {...props} />;
};

export default Icon;
